import { lowerFirst } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SlidingPanel, Tabs } from 'core/components';
import { UpdatedAtSubtitle } from 'core/elements';
import { QuoteDocuments, QuoteNote } from 'quotes/components/QuoteDetails';
import { QuoteEntityType, QuoteNoteType } from 'quotes/enums';
import { ICustomerQuote } from 'quotes/types';
import { getQuoteNoteContent, getQuoteVersionNumber, isOrderEditLocked } from 'quotes/utils';
import { Color } from 'theme/enums';

import { baseInfoWrapperStyle } from './style';
import {
  CustomerQuoteBaseInformation,
  CustomerQuoteCosting,
  ILoadingFields as ICustomerQuoteBaseInformationLoadingFields,
} from './Tabs';

interface ILoadingFields extends ICustomerQuoteBaseInformationLoadingFields {}

export enum CustomerQuoteDetailsTab {
  BASE_INFORMATION = 'baseInformation',
  COSTING = 'costing',
  CUSTOMER_NOTE = 'customerNote',
  DOCUMENTS = 'documents',
  INTERNAL_NOTE = 'internalNote',
}

interface IProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  initialQuote: ICustomerQuote;
  setQuote: (quote: ICustomerQuote) => void;
  entityType?: QuoteEntityType;
  defaultTab?: CustomerQuoteDetailsTab;
  loadingFields?: ILoadingFields;
}

export const CustomerQuoteDetails = ({
  isOpen,
  setIsOpen,
  initialQuote,
  setQuote,
  entityType = QuoteEntityType.QUOTE,
  defaultTab,
  loadingFields,
}: IProps) => {
  const { t } = useTranslation();

  const onClose = () => setIsOpen(false);

  const isQuoteEditable = useMemo(
    () => QuoteEntityType.ORDER !== entityType || !isOrderEditLocked(initialQuote?.status),
    [entityType, initialQuote?.status]
  );

  const tabs = useMemo(
    () => [
      {
        value: CustomerQuoteDetailsTab.BASE_INFORMATION,
        label: t('labelBaseInformation'),
        content: (
          <CustomerQuoteBaseInformation
            quote={initialQuote}
            setQuote={setQuote}
            entityType={entityType}
            isEditable={isQuoteEditable}
            loadingFields={loadingFields}
          />
        ),
        style: baseInfoWrapperStyle,
      },
      {
        value: CustomerQuoteDetailsTab.COSTING,
        label: t('labelCosting'),
        content: <CustomerQuoteCosting quote={initialQuote} />,
      },
      {
        value: CustomerQuoteDetailsTab.INTERNAL_NOTE,
        label: t('labelQuoteInternalNote'),
        content: (
          <QuoteNote
            quote={initialQuote}
            setQuote={setQuote}
            type={QuoteNoteType.INTERNAL_NOTE}
            entityType={entityType}
          />
        ),
        badgeProps: getQuoteNoteContent(initialQuote, QuoteNoteType.INTERNAL_NOTE)
          ? { color: Color.PRIMARY }
          : undefined,
      },
      {
        value: CustomerQuoteDetailsTab.CUSTOMER_NOTE,
        label: t('labelQuoteCustomerNote'),
        content: (
          <QuoteNote
            quote={initialQuote}
            setQuote={setQuote}
            type={QuoteNoteType.CUSTOMER_NOTE}
            entityType={entityType}
            isEditable={isQuoteEditable}
          />
        ),
        badgeProps: getQuoteNoteContent(initialQuote, QuoteNoteType.CUSTOMER_NOTE)
          ? { color: Color.DANGER }
          : undefined,
      },
      {
        value: CustomerQuoteDetailsTab.DOCUMENTS,
        label: t('labelDocuments'),
        content: <QuoteDocuments quote={initialQuote} entityType={entityType} />,
      },
    ],
    [initialQuote, setQuote, entityType, isQuoteEditable, loadingFields, t]
  );

  return (
    <SlidingPanel
      isOpen={isOpen}
      onClosePanel={onClose}
      title={t('titleQuoteDetails', {
        id: initialQuote?.id,
        version: getQuoteVersionNumber(initialQuote),
        entityType,
      })}
      subtitle={<UpdatedAtSubtitle updatedAt={initialQuote?.updatedAt} />}
      autoWidth
    >
      <Tabs tabs={tabs} ariaLabel={`${lowerFirst(entityType)}Details`} defaultTab={defaultTab} />
    </SlidingPanel>
  );
};
